import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Container, Paper, Button, Alert, Snackbar, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { CouponDesignerContainer } from '../../business/CouponDesigner/CouponDesignerContainer';
import { CouponDesign, DesignElement, ElementStyle } from '../../../types/coupon';
import api from '../../../services/api';
import { v4 as uuidv4 } from 'uuid';

// Default style properties to ensure consistency
const defaultElementStyle: ElementStyle = {
    fontFamily: 'Arial',
    fontWeight: 400,
    fontSize: 16,
    color: '#000000',
    backgroundColor: 'transparent',
    textAlign: 'left',
    visibility: 'visible',
    pointerEvents: 'auto',
    zIndex: 0,
    opacity: 1
};

const CouponDesignerWrapper: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [initialDesign, setInitialDesign] = useState<CouponDesign | undefined>();
    const [isLoading, setIsLoading] = useState(true);
    const [isDirty, setIsDirty] = useState(false);
    const [currentDesign, setCurrentDesign] = useState<CouponDesign | undefined>();
    const [currentMarketingSettings, setCurrentMarketingSettings] = useState<any>();

    // Ensure each element has a unique ID and complete style properties
    const ensureElementProperties = (design: CouponDesign): CouponDesign => {
        return {
            ...design,
            elements: design.elements.map(element => ({
                ...element,
                id: element.id || uuidv4(),
                style: {
                    ...defaultElementStyle,
                    ...(element.style || {}),
                    // Preserve any additional custom style properties
                    ...(element.style && Object.keys(element.style).reduce((acc, key) => {
                        if (element.style && element.style[key] !== undefined) {
                            acc[key] = element.style[key];
                        }
                        return acc;
                    }, {} as Record<string, any>))
                }
            }))
        };
    };

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            api.get(`/admin/coupons/${id}`)
                .then(response => {
                    if (response.data?.data?.coupon?.design) {
                        const designWithProperties = ensureElementProperties(response.data.data.coupon.design);
                        setInitialDesign(designWithProperties);
                    } else {
                        throw new Error('No design data found');
                    }
                })
                .catch(error => {
                    console.error('Error loading coupon design:', error);
                    setError('Failed to load coupon design');
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [id]);

    const handleDesignChange = (design: CouponDesign) => {
        setCurrentDesign(design);
        setIsDirty(true);
    };

    const handleMarketingSettingsChange = (settings: any) => {
        setCurrentMarketingSettings(settings);
        setIsDirty(true);
    };

    const handleSave = async (design: CouponDesign, marketingSettings: any, shouldRedirect: boolean = false) => {
        try {
            setError(null);
            const designWithProperties = ensureElementProperties(design);
            
            const response = await api.patch(`/admin/coupons/${id}`, { 
                design: designWithProperties,
                marketingSettings 
            });
            
            if (response.data?.data?.coupon) {
                setSuccess(true);
                setIsDirty(false);
                
                // Only redirect if explicitly requested (i.e., Save Design button click)
                if (shouldRedirect) {
                    setTimeout(() => {
                        navigate('/admin/coupons');
                    }, 1500);
                }
            } else {
                throw new Error('Invalid response format from server');
            }
        } catch (error: any) {
            console.error('Error saving coupon design:', error);
            setError(error.response?.data?.message || 'Failed to save coupon design');
        }
    };

    return (
        <Container maxWidth={false}>
            <Box sx={{ py: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/admin/coupons')}
                    >
                        Back to Coupons
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={() => currentDesign && handleSave(currentDesign, currentMarketingSettings, true)}
                        disabled={!isDirty}
                    >
                        Save Design
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Snackbar
                    open={success}
                    autoHideDuration={3000}
                    onClose={() => setSuccess(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert severity="success">Design saved successfully!</Alert>
                </Snackbar>

                {isLoading ? (
                    <Box display="flex" justifyContent="center" my={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Paper sx={{ p: 0, overflow: 'hidden' }}>
                        <CouponDesignerContainer
                            initialDesign={initialDesign}
                            onChange={handleDesignChange}
                            couponId={id}
                        />
                    </Paper>
                )}
            </Box>
        </Container>
    );
};

export default CouponDesignerWrapper;
